import { AlertTypes } from "../types/system/AlertEntry";
import DatastoreRendering from "../communication/DatastoreRendering";
import store, { IAppState } from "../state/Store";
import { addAlert } from "../state/system/Alerts";
import {
    InputFormat,
    PrintTarget,
    RenderPrintConfiguration,
    SpreadsheetEntry,
    SpreadsheetRow,
} from "../types/system/PrintConfiguration";
import {
    HtmlPackage,
    TextPackage,
    TablePackage,
    RenderPackage,
} from "../types/system/RenderTypes";
import Logger from "./Logger";
import Utility from "./Utility";

import printStyles from "../styles/print.resource";

export default class Rendering {
    static style: string = null;
    static async getStyles() {
        if (!this.style) {
            try {
                const response = await fetch(printStyles);
                if (response.ok) this.style = await response.text();
            } catch (error) {
                Logger.error("Failed to load print styles");
                Logger.error(error);
            }
        }

        return this.style;
    }

    static getReportTitle(title: string | null = null) {
        if (!!title) return title;

        let temp = document.querySelector(".report-title")?.textContent ?? "";

        temp = temp.replace(/\n/g, "");
        while (temp.indexOf("  ") >= 0) {
            temp = temp.replace(/ {2}/g, " ");
        }

        return temp.trim();
    }

    private static getNodeText(text: string) {
        const temp = document.createElement("div");
        temp.textContent = text;
        return temp.innerText.trim();
    }

    private static extractContent(
        element: HTMLElement,
        selector: string,
        indent: string,
    ) {
        element.querySelectorAll(selector).forEach(el => {
            const newSpan = document.createElement("span");
            newSpan.innerHTML =
                (el.classList.contains("noIndent") ? "" : indent) +
                el.innerHTML.trim();
            newSpan.append(document.createElement("br"));
            el.parentElement.parentElement.append(newSpan);
            el.remove();
        });
    }

    private static async promptLandscape() {
        const config = store.getState().db.ui;
        if (
            config.promptLandscape === true &&
            config.printConfig.pageOrientation === 0
        ) {
            const result = await addAlert({
                type: AlertTypes.Confirm,
                text:
                    "Landscape page orientation is recommended. " +
                    "Do you want to change to Landscape?",
                okText: "Yes",
                cancelText: "No",
            });

            if (result.response) config.printConfig.pageOrientation = 1;
        }
    }

    private static compileConfiguration(): RenderPrintConfiguration {
        const config = store.getState().db.ui.printConfig;

        return {
            schoolFont: {
                name: config.schoolFont.name,
                size: String(config.schoolFont.size),
                weight: String(config.schoolFont.weight),
                decoration: config.schoolFont.decoration,
            },
            tableHeadFont: {
                name: config.tableHeadFont.name,
                size: String(config.tableHeadFont.size),
                weight: String(config.tableHeadFont.weight),
                decoration: config.tableHeadFont.decoration,
            },
            tableFont: {
                name: config.tableFont.name,
                size: String(config.tableFont.size),
                weight: String(config.tableFont.weight),
                decoration: config.tableFont.decoration,
            },
            margin: {
                top: config.margin.top,
                left: config.margin.left,
                bottom: config.margin.bottom,
                right: config.margin.right,
            },
            addColumns: config.addColumns,
            noColumns: config.noColumns,
            colWidth: config.colWidth,
            pageOrientation: config.pageOrientation,
            separatePages: config.separatePages,
            printDate: new Date().toISOString(),
        };
    }

    private static async getCustomData<T>(
        target: PrintTarget,
        method: "Table" | "Text",
    ) {
        const config = store.getState().db.ui;

        if (!config.printSupplementary && config.customPrintMethod) {
            if (config.promptAll) {
                const result = await addAlert({
                    type: AlertTypes.Confirm,
                    text:
                        "Do you want to print all information " +
                        "or only what is currently displayed?",
                    okText: "Displayed Only",
                    cancelText: "All Information",
                });

                if (!result.response)
                    return (await config.customPrintMethod?.(
                        target,
                        method,
                    )) as T;
            } else
                return (await config.customPrintMethod?.(target, method)) as T;
        }

        return null;
    }

    private static async getCustomConfig(
        target: PrintTarget,
        type: InputFormat,
    ) {
        const config = store.getState().db.ui;

        await this.promptLandscape();

        const customData = await this.getCustomData<
            string | Array<string> | Array<SpreadsheetEntry>
        >(target, type === InputFormat.Table ? "Table" : "Text");

        if (customData !== "Cancel Print") {
            return {
                title: config.customPrintTitle,
                file: config.customPrintName,
                container:
                    (config.customPrintElement
                        ? document.querySelector<HTMLElement>(
                              config.customPrintElement,
                          )
                        : null) ??
                    (config.printSupplementary
                        ? document.querySelector<HTMLElement>(
                              ".extra-detail-content",
                          )
                        : null),
                data: customData,
            };
        }

        return null;
    }

    private static async renderPageHeader(title: string = null) {
        let header = "";
        if (Utility.notNull(title)) {
            document.querySelectorAll("body .school-title").forEach(element => {
                header = header + element.innerHTML + "\n";
            });

            header =
                '<div class="page-header-title">' +
                '<div class="school-title">' +
                header +
                '</div><div class="page-title">' +
                title +
                "</div></div>";
        } else {
            document.querySelectorAll("body .page-header").forEach(element => {
                header = header + element.innerHTML + "\n";
            });

            header = '<div class="page-header-title">' + header + "</div>";
        }

        return header;
    }

    static renderPageReportContent(
        customData: string = null,
        container: HTMLElement = null,
        configuration: RenderPrintConfiguration = null,
    ) {
        let body = "";

        if (!customData) {
            container = container ?? document.querySelector(".tab-content");

            const report = container.querySelector(
                ".tts-report-container-content",
            ) as HTMLElement;
            if (report) container = report;

            const content = container.cloneNode(true) as HTMLElement;
            this.addAdditionalColumns(content, configuration);
            body = body + this.processHTML(content.innerHTML) + "\n";
        }

        return body;
    }

    private static processHTML(html: string) {
        let result = "";
        for (let index = 0; index < html.length; index++) {
            const charCode = html.charCodeAt(index);
            if (charCode > 9727 && charCode < 9984) result += `&#${charCode};`;
            else result += html[index];
        }
        return result;
    }

    private static addAdditionalColumns(
        content: HTMLElement,
        configuration: RenderPrintConfiguration,
    ) {
        if (configuration?.addColumns)
            content.querySelectorAll("table tr").forEach(row => {
                for (let index = 0; index < configuration.noColumns; index++) {
                    const td = document.createElement("td");
                    td.style.width = `${
                        configuration.colWidth * 3.779527559
                    }px`;
                    row.appendChild(td);
                }
            });
    }

    //PDF rendering
    static async renderPdf(data: HtmlPackage, fileName: string) {
        const { school, ui } = store.getState().db;
        return await DatastoreRendering.post(
            "Render/PDF" +
                (fileName ? "?customName=" + encodeURIComponent(fileName) : ""),
            {
                SchoolName: school.school.name,
                FileName:
                    fileName ??
                    (ui?.currentFile
                        ? ui.currentFile.toString()
                        : school.school.name),
                CustomName: fileName,
                Content: data,
            } as RenderPackage,
        );
    }

    static async renderPageToPdf(
        title: string,
        customData: string,
        container: HTMLElement,
        fileName: string,
    ) {
        try {
            const header = await this.renderPageHeader(title);
            const configuration = this.compileConfiguration();

            let body = "";

            await Utility.sleep();
            if (!customData) {
                container = container ?? document.querySelector(".tab-content");

                const content = container.cloneNode(true) as HTMLElement;
                this.addAdditionalColumns(content, configuration);
                body = body + this.processHTML(content.innerHTML) + "\n";
            }

            const result = await this.renderPdf(
                {
                    Name: "",
                    Styles: `<style>${await this.getStyles()}</style>`,
                    Body: customData ?? body,
                    Header: header,
                    Footer: "",
                    Configuration: configuration,
                },
                fileName,
            );

            return result;
        } catch (error) {
            Logger.error(error);
        }

        return null;
    }

    static async downloadPageToPdf(
        title: string,
        customData: string,
        container: HTMLElement,
        fileName: string,
    ) {
        const result = await this.renderPageToPdf(
            title,
            customData,
            container,
            fileName,
        );

        result.download(
            fileName ?? `${this.getReportTitle(title).replace(/:/g, "-")}.pdf`,
        );
    }

    //Compile HTML page down into data fit for export
    static async prepTableContent(
        element: HTMLElement,
        title: string,
        forExcelReport: boolean = false,
    ) {
        await Utility.sleep();

        const result = new Array<SpreadsheetEntry>();
        const tables = element.querySelectorAll("table");

        tables.forEach((table, tableIndex) => {
            let tableName = table.getAttribute("table-name");

            let nData = {
                Name: "",
                TabName: tableName ?? "Table " + (tableIndex + 1),
                Rows: [],
            } as SpreadsheetEntry;

            if (forExcelReport)
                if (!!tableName) nData.Name = tableName;
                else if (tables.length > 1)
                    nData.Name = "Table " + (tableIndex + 1);
                else nData.Name = title;
            else if (Utility.notNull(tableName)) nData.Name = tableName;
            else if (tables.length > 1)
                nData.Name = "Table " + (tableIndex + 1);
            else nData.Name = "";

            table
                .querySelectorAll("tr:not(.noPrint):not(.filterRow)")
                .forEach(row => {
                    if (!(row as unknown as any).checkVisibility()) return;

                    const rData = {
                        Classes: row.className.replace(" ", ""),
                        Cells: [],
                    } as SpreadsheetRow;

                    row.querySelectorAll(
                        "td:not(.noPrint),th:not(.noPrint)",
                    ).forEach((cell, callIndex) => {
                        if (!(cell as unknown as any).checkVisibility()) return;

                        let check = cell.querySelector<HTMLInputElement>(
                            'input[type="checkbox"],input.triStateControlObject,input[type="radio"]',
                        );
                        check = !(check as unknown as any)?.checkVisibility()
                            ? null
                            : check;

                        // remove noPrint elements
                        const cellClone = cell.cloneNode(true) as HTMLElement;
                        cellClone
                            .querySelectorAll(".noPrint")
                            .forEach(el => el.remove());
                        cellClone.querySelectorAll("br").forEach(el => {
                            const textNode = document.createTextNode("\n");
                            el.replaceWith(textNode);
                        });

                        // remove sorting css class names
                        let cellValue = cellClone.textContent
                            .trim()
                            .replace("expand_more", "")
                            .replace("expand_less", "");

                        if (
                            Utility.notNull(forExcelReport) &&
                            cellValue.isDate()
                        )
                            cellValue = new Date(cellValue).toServer().toJSON();

                        rData.Cells[callIndex] = {
                            Value: "",
                            Classes: cell.className.replace(" ", ""),
                        };

                        if (cellValue && cellValue.length > 0)
                            rData.Cells[callIndex].Value = cellValue;
                        else if (check && check.style.visibility !== "hidden")
                            if (
                                check.getAttribute("alt") === "Checked" ||
                                check.checked
                            )
                                rData.Cells[callIndex].Value = "Yes";
                            else rData.Cells[callIndex].Value = "No";
                    });

                    nData.Rows.push(rData);
                });

            result.push(nData);
        });

        return result;
    }

    //Excel rendering
    static async renderPageToExcel(
        title: string,
        customData: Array<SpreadsheetEntry>,
        container: HTMLElement,
        fileName: string,
    ) {
        try {
            const { school, ui } = store.getState().db;
            let reportTitle = "";
            let data = customData;

            if (!data) {
                reportTitle = this.getReportTitle(title);
                data = await this.prepTableContent(
                    (container =
                        container ?? document.querySelector(".tab-content")),
                    reportTitle,
                    true,
                );
            }

            if (Utility.notNull(data)) {
                if (data.length === 1) {
                    data[0].Name = "";
                } else {
                    data.forEach(d => {
                        if (
                            d.Name ===
                            (reportTitle ?? d.Name.startsWith("Table"))
                        )
                            d.Name = "";
                    });
                }
            }

            const configuration = await this.compileConfiguration();

            return await DatastoreRendering.post(
                "Render/XLS" +
                    (fileName
                        ? "?customName=" + encodeURIComponent(fileName)
                        : ""),
                {
                    SchoolName: school.school.name,
                    FileName:
                        fileName ??
                        (ui?.currentFile
                            ? ui.currentFile.toString()
                            : school.school.name),
                    CustomName: fileName,
                    Content: {
                        School: school.school.name,
                        Title: reportTitle,
                        Tables: data,
                        Configuration: configuration,
                    } as TablePackage,
                } as RenderPackage,
            );
        } catch (error) {
            throw error;
        }
    }

    static async downloadPageToExcel(
        title: string,
        customData: Array<SpreadsheetEntry>,
        container: HTMLElement,
        fileName: string,
    ) {
        try {
            const result = await this.renderPageToExcel(
                title,
                customData,
                container,
                fileName,
            );

            result.download(
                this.getReportTitle(title).replace(/:/g, "-") + ".xlsx",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            );
        } catch (error) {
            Logger.error(error);
        }
    }

    static async prepContentForWord(element: HTMLElement, useNill: boolean) {
        try {
            await Utility.sleep();

            let result = "";
            const noDetails = element.querySelector(".no-details:visible");

            if (noDetails) {
                result =
                    result + (useNill ? "Nil" : noDetails.textContent.trim());
            } else {
                const temp = element.cloneNode(false) as HTMLElement;

                temp.querySelectorAll(".tts-report-toolbar-container").forEach(
                    el => el.remove(),
                );
                temp.querySelectorAll(".tts-report-header").forEach(el =>
                    el.remove(),
                );
                temp.querySelectorAll(".tts-report-footer").forEach(el =>
                    el.remove(),
                );
                temp.querySelectorAll(".IndentedContentTop").forEach(el => {
                    const newSpan = document.createElement("span");
                    newSpan.append(...el.childNodes);
                    el.parentElement!.append(newSpan);
                    el.remove();
                });
                temp.querySelectorAll(".IndentedContent").forEach(el => {
                    const newUl = document.createElement("ul");
                    const newLi = document.createElement("li");
                    newLi.appendChild(el);
                    newUl.appendChild(newLi);
                    el.parentElement!.appendChild(newUl);
                });
                temp.querySelectorAll(".heading").forEach(el => {
                    const newH3 = document.createElement("h3");
                    newH3.textContent = el.textContent;
                    el.parentElement!.insertBefore(newH3, el);
                    el.remove();
                });

                this.extractContent(
                    temp,
                    "ul li ul li,ol li ol li,ul li ol li,ol li ul li",
                    "&nbsp;".repeat(24),
                );
                this.extractContent(temp, "ul li,ol li", "&nbsp;".repeat(6));
                temp.querySelectorAll("ul").forEach(el => el.remove());

                let text = temp.innerHTML
                    .replace(/\n/g, "")
                    .replace(/\r/g, "")
                    .replace(/<!--[\s\S]*?-->/g, "")
                    .replace(/>[\s]*</g, "><")
                    .trim();

                while (text.indexOf("  ") > 0)
                    text = text.replace(/ {2}/g, " ");

                result = result + text;
            }

            const output = new Array<string>();
            if (result.indexOf("--page-break--") >= 0)
                for (const split of result.matchAll(
                    /(.*?)<div[\s\-\d\w=":;!.()'&|]+?>\s*--page-break--\s*<\/div>/g,
                )) {
                    const text = this.getNodeText(split[1]);
                    if (!!split[1] && !!text) output.push(text);
                }

            return !!output.length ? output : [result];
        } catch (error) {
            throw error;
        }
    }

    static async renderPageTextToWord(
        title: string,
        customData: string | Array<string>,
        container: HTMLElement,
        fileName: string,
    ) {
        try {
            const { school, ui } = store.getState().db;
            let data = customData;

            if (!data)
                data = await this.prepContentForWord(
                    container ?? document.querySelector(".tab-content"),
                    true,
                );

            const configuration = await this.compileConfiguration();

            return await DatastoreRendering.post(
                "Render/DOC/TBL" +
                    (fileName
                        ? "?customName=" + encodeURIComponent(fileName)
                        : ""),
                {
                    SchoolName: school.school.name,
                    FileName:
                        fileName ??
                        (ui?.currentFile
                            ? ui.currentFile.toString()
                            : school.school.name),
                    CustomName: fileName,
                    Content: {
                        School: school.school.name,
                        Title: title,
                        Styles: "",
                        Text: data instanceof Array ? data : [data],
                        Configuration: configuration,
                    } as TextPackage,
                } as RenderPackage,
            );
        } catch (error) {
            Logger.error(error);
        }

        return null;
    }

    //Word rendering
    static async renderPageToWord(
        title: string,
        customData: Array<SpreadsheetEntry>,
        container: HTMLElement,
        fileName: string,
    ) {
        try {
            const { school, ui } = store.getState().db;
            let reportTitle = "";
            let data = customData;

            if (!data) {
                reportTitle = this.getReportTitle(title);
                data = await this.prepTableContent(
                    container ??
                        document.querySelector<HTMLElement>(".tab-content"),
                    reportTitle,
                );
            }

            const configuration = await this.compileConfiguration();

            return await DatastoreRendering.post(
                "Render/DOC/TBL" +
                    (fileName
                        ? "?customName=" + encodeURIComponent(fileName)
                        : ""),
                {
                    SchoolName: school.school.name,
                    FileName:
                        fileName ??
                        (ui?.currentFile
                            ? ui.currentFile.toString()
                            : school.school.name),
                    CustomName: fileName,
                    Content: {
                        School: school.school.name,
                        Title: title,
                        Styles: "",
                        Tables: data,
                        Configuration: configuration,
                    } as TablePackage,
                } as RenderPackage,
            );
        } catch (error) {
            throw error;
        }
    }

    static async downloadPageTextToWord(
        title: string,
        customData: string | Array<string>,
        container: HTMLElement,
        fileName: string,
    ) {
        try {
            let result = await this.renderPageTextToWord(
                title,
                customData,
                container,
                fileName,
            );

            result.download(
                this.getReportTitle(title).replace(/:/g, "-") + ".docx",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            );
        } catch (error) {
            Logger.error(error);
        }
    }

    static async downloadPageToWord(
        title: string,
        customData: Array<SpreadsheetEntry>,
        container: HTMLElement,
        fileName: string,
    ) {
        try {
            const result = await this.renderPageToWord(
                title,
                customData,
                container,
                fileName,
            );

            result.download(
                this.getReportTitle(title).replace(/:/g, "-") + ".docx",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            );
        } catch (error) {
            Logger.error(error);
        }
    }

    static async preview() {
        try {
            const config = await this.getCustomConfig(
                PrintTarget.PDF,
                InputFormat.Text,
            );

            // await this.autoPagerService.setPrintFormat();

            const result = await this.renderPageToPdf(
                config?.title ?? "",
                config?.data as string,
                config?.container ??
                    document.querySelector<HTMLElement>(".tab-frame"),
                config?.file,
            );

            return result?.toDataUrl("application/pdf");
        } finally {
            // await this.autoPagerService.unSetPrintFormat();
        }
    }

    static async exportPDF() {
        try {
            const config = await this.getCustomConfig(
                PrintTarget.PDF,
                InputFormat.Text,
            );

            // await this.autoPagerService.setPrintFormat();

            await this.downloadPageToPdf(
                config.title,
                config.data as string,
                config.container,
                config.file,
            );
        } finally {
            // await this.autoPagerService.unSetPrintFormat();
        }
    }

    static async exportXLS() {
        try {
            const config = await this.getCustomConfig(
                PrintTarget.XLS,
                InputFormat.Table,
            );

            // await this.autoPagerService.setPrintFormat();

            await this.downloadPageToExcel(
                config.title,
                config.data as Array<SpreadsheetEntry>,
                config.container,
                config.file,
            );
        } finally {
            // await this.autoPagerService.unSetPrintFormat();
        }
    }

    static async exportDOC() {
        try {
            const toText = store.getState().db.ui.wordAsText;
            const format = toText ? InputFormat.Text : InputFormat.Table;
            const config = await this.getCustomConfig(PrintTarget.DOC, format);

            // await this.autoPagerService.setPrintFormat();

            if (toText) {
                await this.downloadPageTextToWord(
                    config.title,
                    config.data as string | Array<string>,
                    config.container,
                    config.file,
                );
            } else {
                await this.downloadPageToWord(
                    config.title,
                    config.data as Array<SpreadsheetEntry>,
                    config.container,
                    config.file,
                );
            }
        } finally {
            // await this.autoPagerService.unSetPrintFormat();
        }
    }

    static async doPrint() {
        try {
            const config = await this.getCustomConfig(
                PrintTarget.PDF,
                InputFormat.Text,
            );

            // await this.autoPagerService.setPrintFormat();

            const result = await this.renderPageToPdf(
                config.title,
                config.data as string,
                config.container,
                config.file,
            );

            let frame = document.getElementById(
                "printFrame",
            ) as HTMLIFrameElement;

            if (!frame) {
                frame = document.createElement("iframe") as HTMLIFrameElement;
                frame.id = "printFrame";
                frame.style.width = "0";
                frame.style.height = "0";
                frame.style.border = "0";
                document
                    .getElementsByClassName("page-elements")[0]
                    .appendChild(frame);
            }
            await new Promise<void>(async resolve => {
                frame.addEventListener("load", () => {
                    frame.contentWindow.print();
                    resolve();
                });

                frame.src = URL.createObjectURL(
                    result.toBlob("application/pdf"),
                );
            });
        } finally {
            // await this.autoPagerService.unSetPrintFormat();
        }
    }
}
